import { useState } from 'react';
import { Link, useLang } from '../i18n';
import SEO from '../components/SEO';
import { ORGANIZATION_SCHEMA, SITE_URL } from '../data/siteData';
import { LEGAL_OWNER } from '../data/legalData';

export default function ContactanosPage() {
  const { t } = useLang();
  const [form, setForm] = useState({ nombre: '', email: '', telefono: '', mensaje: '', privacidad: false });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const contactInfo = [
    {
      bg: 'ci-teal',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
      title: t('Email de soporte', 'Correu de suport'),
      lines: ['info@speakenglishnow.club'],
    },
    {
      bg: 'ci-purple',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13 19.79 19.79 0 0 1 1.61 4.38 2 2 0 0 1 3.58 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.18 6.18l1.17-1.17a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
      title: t('Teléfono / WhatsApp', 'Telèfon / WhatsApp'),
      lines: ['+34 650 492 859'],
    },
    {
      bg: 'ci-teal',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
      title: t('Horario de atención', 'Horari d\'atenció'),
      lines: [t('Lunes a viernes: 9:00 – 20:00', 'Dilluns a divendres: 9:00 – 20:00')],
    },
    {
      bg: 'ci-purple',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
      title: t('Dónde damos clase', 'On fem classe'),
      lines: [t('Presenciales: Mataró, Barcelona y alrededores', 'Presencials: Mataró, Barcelona i voltants'), t('Online: Cataluña, España y otros países', 'En línia: Catalunya, Espanya i altres països')],
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const data = new URLSearchParams({ 'form-name': 'contacto', ...form });
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (!response.ok) throw new Error('No se pudo enviar el formulario');
      setSent(true);
    } catch {
      setError(t('No se ha podido enviar el mensaje. Prueba de nuevo o escríbenos por WhatsApp.', 'No s\'ha pogut enviar el missatge. Torna-ho a provar o escriu-nos per WhatsApp.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO title={t('Contacto para clases de inglés', 'Contacte per a classes d\'anglès')} description={t('Contacta con Speak English Now para informarte sobre clases de inglés online o presenciales en Mataró, Barcelona y alrededores.', 'Contacta amb Speak English Now per informar-te sobre classes d\'anglès en línia o presencials a Mataró, Barcelona i voltants.')} canonical={`${SITE_URL}/contacto`} schema={{ '@context': 'https://schema.org', '@graph': [ORGANIZATION_SCHEMA, { '@type': 'ContactPage', name: t('Contacto de Speak English Now', 'Contacte de Speak English Now'), url: `${SITE_URL}/contacto`, inLanguage: t('es-ES', 'ca-ES'), about: { '@id': `${SITE_URL}/#organization` } }] }} />
      {/* HERO */}
      <section className="page-hero">
        <div className="container text-center">
          <span className="badge-pill">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            {t('CONTACTO', 'CONTACTE')}
          </span>
          <h1 className="page-hero-title">{t('Contáctanos', 'Contacta\'ns')}</h1>
          <p className="page-hero-sub">{t('¿Quieres mejorar tu inglés pero no sabes qué opción es mejor para ti? Cuéntanos qué necesitas y te ayudaremos a encontrar la opción que mejor encaje contigo: SEN Pro, clases online, presenciales o para empresas.', 'Vols millorar el teu anglès però no saps quina opció és millor per a tu? Explica\'ns què necessites i t\'ajudarem a trobar l\'opció que millor encaixi amb tu: SEN Pro, classes en línia, presencials o per a empreses.')}</p>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="section-pad">
        <div className="container">
          <div className="contact-grid">
            {/* FORM */}
            <div className="contact-form-card">
              <h2 className="contact-form-title">{t('Envíanos un mensaje', 'Envia\'ns un missatge')}</h2>
              {sent ? (
                <div className="form-success">
                  <div style={{ fontSize: 48 }}>✅</div>
                  <h3>{t('¡Mensaje enviado!', 'Missatge enviat!')}</h3>
                  <p>{t('Te responderemos en menos de 24 horas.', 'Et respondrem en menys de 24 hores.')}</p>
                </div>
              ) : (
                <form name="contacto" method="POST" data-netlify="true" data-netlify-honeypot="bot-field" onSubmit={handleSubmit} className="contact-form">
                  <input type="hidden" name="form-name" value="contacto" />
                  <p hidden><label>No rellenes este campo: <input name="bot-field" /></label></p>
                  <div className="form-field">
                    <label>{t('Nombre *', 'Nom *')}</label>
                    <input name="nombre" type="text" placeholder={t('Tu nombre completo', 'El teu nom complet')} required value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})} />
                  </div>
                  <div className="form-field">
                    <label>{t('Email *', 'Correu electrònic *')}</label>
                    <input name="email" type="email" placeholder="tu@email.com" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                  </div>
                  <div className="form-field">
                    <label>{t('Teléfono', 'Telèfon')}</label>
                    <input name="telefono" type="tel" placeholder="+34 000 123 456" value={form.telefono} onChange={e => setForm({...form, telefono: e.target.value})} />
                  </div>
                  <div className="form-field">
                    <label>{t('Mensaje *', 'Missatge *')}</label>
                    <textarea name="mensaje" rows={5} placeholder={t('¿En qué podemos ayudarte?', 'En què et podem ajudar?')} required value={form.mensaje} onChange={e => setForm({...form, mensaje: e.target.value})} />
                  </div>
                  <label className="legal-consent">
                    <input name="privacidad" type="checkbox" required checked={form.privacidad} onChange={e => setForm({...form, privacidad: e.target.checked})} />
                    <span>{t('He leído la', 'He llegit la')} <Link to="/politica-privacidad">{t('Política de Privacidad', 'Política de Privacitat')}</Link>. {t('Mis datos se usarán para responder a esta consulta.', 'Les meves dades s\'utilitzaran per respondre a aquesta consulta.')}</span>
                  </label>
                  <p className="form-privacy-layer">{t(`Responsable: ${LEGAL_OWNER.legalName}, bajo el nombre Speak English Now. Finalidad: atender tu solicitud. Base jurídica: medidas precontractuales o consentimiento, según la consulta. Derechos: acceso, rectificación, supresión y demás derechos indicados en la política.`, `Responsable: ${LEGAL_OWNER.legalName}, sota el nom Speak English Now. Finalitat: atendre la teva sol·licitud. Base jurídica: mesures precontractuals o consentiment, segons la consulta. Drets: accés, rectificació, supressió i altres drets indicats a la política.`)}</p>
                  {error && <p className="form-error" role="alert">{error}</p>}
                  <button type="submit" className="btn-purple-full w-full" disabled={submitting}>
                    {submitting ? t('Enviando…', 'Enviant…') : t('Enviar mensaje', 'Enviar missatge')}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  </button>
                </form>
              )}
            </div>

            {/* INFO */}
            <div className="contact-info">
              <h2 className="contact-form-title">{t('Información de contacto', 'Informació de contacte')}</h2>
              <div className="contact-info-list">
                {contactInfo.map(ci => (
                  <div key={ci.bg + ci.lines[0]} className="contact-info-item">
                    <div className={`ci-icon ${ci.bg}`}>{ci.icon}</div>
                    <div>
                      <h4>{ci.title}</h4>
                      {ci.lines.map(l => <p key={l}>{l}</p>)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="contact-immediate">
                <h4>{t('¿Prefieres hablar por WhatsApp?', 'Prefereixes parlar per WhatsApp?')}</h4>
                <p>{t('Escríbenos y te ayudaremos a decidir qué opción encaja mejor contigo.', 'Escriu-nos i t\'ajudarem a decidir quina opció encaixa millor amb tu.')}</p>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <a href="https://wa.me/34650492859" className="btn-teal-sm">WhatsApp</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad">
        <div className="container">
          <div className="cta-box">
            <h2 className="cta-title">{t('¿Aún no sabes qué opción es para ti?', 'Encara no saps quina opció és per a tu?')}</h2>
            <p className="cta-sub">{t('Descubre nuestras dos formas principales de aprender inglés y elige la que mejor encaje contigo.', 'Descobreix les nostres dues maneres principals d\'aprendre anglès i tria la que millor encaixi amb tu.')}</p>
            <div className="cta-btns">
              <Link to="/curso-online" className="btn-cta-white">{t('Descubre SEN Pro', 'Descobreix SEN Pro')}</Link>
              <Link to="/clases" className="btn-cta-outline">{t('Ver clases', 'Veure classes')}</Link>
            </div>
            <p className="cta-promo">{t('¿Prefieres que te ayudemos a decidir? Escríbenos por WhatsApp.', 'Prefereixes que t\'ajudem a decidir? Escriu-nos per WhatsApp.')}</p>
          </div>
        </div>
      </section>
    </>
  );
}
